import React, { useContext } from 'react';
import Head from 'next/head';
import { Link } from 'react-router-dom';
import { Box } from '@hub/box';
import { CoreContainer } from '@hub/core-container';
import { H1 } from '@hub/heading';
import { BasicPageContext, CentreContext, CentresContext, NavContext } from '@scentregroup/shared/context';
import { UniversalFooter } from '../../components/universal-footer';
import CVPStrip from '../cvp-strip';
import { useUniversalHeader } from '../universal-header';
import { completelyCanonicalUrl } from '../../helpers/canonical-link';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

function safeMeta(value) {
  return typeof value === 'string' && value === '' ? undefined : value;
}

export default function Layout(_ref) {
  var _metaData$twitter, _metaData$twitter2, _safeMeta, _metaData$openGraph, _safeMeta2, _metaData$openGraph2, _safeMeta3, _metaData$openGraph3, _metaData$openGraph4, _metaData$twitter3, _header$data$centres, _header$data;

  let {
    country,
    centre,
    header,
    metaData,
    pageTitle,
    showCVPStrip = true,
    showFooter = true,
    children,
    mainStyles
  } = _ref;
  const [Header, setHeaderNavigationPath] = useUniversalHeader(header);
  const showChrome = !useContext(BasicPageContext);
  const canonical = safeMeta(metaData === null || metaData === void 0 ? void 0 : metaData.canonical);
  const title = safeMeta(metaData === null || metaData === void 0 ? void 0 : metaData.title);
  const description = safeMeta(metaData === null || metaData === void 0 ? void 0 : metaData.description);
  let twitterSite = safeMeta(metaData === null || metaData === void 0 ? void 0 : (_metaData$twitter = metaData.twitter) === null || _metaData$twitter === void 0 ? void 0 : _metaData$twitter.site);

  if (country === 'Australia') {
    var _twitterSite;

    (_twitterSite = twitterSite) !== null && _twitterSite !== void 0 ? _twitterSite : twitterSite = '@WestfieldAU';
  } else if (country === 'New Zealand') {
    var _twitterSite2;

    (_twitterSite2 = twitterSite) !== null && _twitterSite2 !== void 0 ? _twitterSite2 : twitterSite = '@WestfieldNZ';
  }

  const twitterCreator = safeMeta(metaData === null || metaData === void 0 ? void 0 : (_metaData$twitter2 = metaData.twitter) === null || _metaData$twitter2 === void 0 ? void 0 : _metaData$twitter2.creator);
  const openGraphUrl = (_safeMeta = safeMeta(metaData === null || metaData === void 0 ? void 0 : (_metaData$openGraph = metaData.openGraph) === null || _metaData$openGraph === void 0 ? void 0 : _metaData$openGraph.url)) !== null && _safeMeta !== void 0 ? _safeMeta : canonical;
  const openGraphTitle = (_safeMeta2 = safeMeta(metaData === null || metaData === void 0 ? void 0 : (_metaData$openGraph2 = metaData.openGraph) === null || _metaData$openGraph2 === void 0 ? void 0 : _metaData$openGraph2.title)) !== null && _safeMeta2 !== void 0 ? _safeMeta2 : title;
  const openGraphDescription = (_safeMeta3 = safeMeta(metaData === null || metaData === void 0 ? void 0 : (_metaData$openGraph3 = metaData.openGraph) === null || _metaData$openGraph3 === void 0 ? void 0 : _metaData$openGraph3.description)) !== null && _safeMeta3 !== void 0 ? _safeMeta3 : description;
  const openGraphImage = safeMeta(metaData === null || metaData === void 0 ? void 0 : (_metaData$openGraph4 = metaData.openGraph) === null || _metaData$openGraph4 === void 0 ? void 0 : _metaData$openGraph4.image);
  let twitterCard = safeMeta(metaData === null || metaData === void 0 ? void 0 : (_metaData$twitter3 = metaData.twitter) === null || _metaData$twitter3 === void 0 ? void 0 : _metaData$twitter3.card);

  if (openGraphImage) {
    var _twitterCard;

    (_twitterCard = twitterCard) !== null && _twitterCard !== void 0 ? _twitterCard : twitterCard = 'summary_large_image';
  } else if (openGraphTitle || openGraphDescription) {
    var _twitterCard2;

    (_twitterCard2 = twitterCard) !== null && _twitterCard2 !== void 0 ? _twitterCard2 : twitterCard = 'summary';
  } // Slightly different from the logic in Westfield Direct layout as it has to deal with legacy canonical support


  const completeCanonical = canonical && metaData !== null && metaData !== void 0 && metaData.canonicalParams ? completelyCanonicalUrl(canonical, metaData.canonicalParams) : canonical;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Head, {
      "data-id": "layout_index_Layout_Head",
      children: [title && /*#__PURE__*/_jsx("title", {
        "data-id": "layout_index_Layout_title",
        children: title
      }), description && /*#__PURE__*/_jsx("meta", {
        name: "description",
        content: description,
        "data-id": "layout_index_Layout_meta"
      }, "description"), twitterSite && /*#__PURE__*/_jsx("meta", {
        name: "twitter:site",
        content: twitterSite,
        "data-id": "layout_index_Layout_meta_3"
      }), twitterCreator && /*#__PURE__*/_jsx("meta", {
        name: "twitter:creator",
        content: twitterCreator,
        "data-id": "layout_index_Layout_meta_4"
      }), twitterCard && /*#__PURE__*/_jsx("meta", {
        name: "twitter:card",
        content: twitterCard,
        "data-id": "layout_index_Layout_meta_5"
      }), openGraphUrl && /*#__PURE__*/_jsx("meta", {
        property: "og:url",
        content: openGraphUrl,
        "data-id": "layout_index_Layout_meta_6"
      }), openGraphTitle && /*#__PURE__*/_jsx("meta", {
        property: "og:title",
        content: openGraphTitle,
        "data-id": "layout_index_Layout_meta_7"
      }), openGraphDescription && /*#__PURE__*/_jsx("meta", {
        property: "og:description",
        content: openGraphDescription,
        "data-id": "layout_index_Layout_meta_8"
      }), openGraphImage && /*#__PURE__*/_jsx("meta", {
        property: "og:image",
        content: openGraphImage,
        "data-id": "layout_index_Layout_meta_9"
      }), completeCanonical && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("meta", {
          property: "og:url",
          content: completeCanonical,
          "data-id": "layout_index_Layout_meta_10"
        }, "og:url"), /*#__PURE__*/_jsx("link", {
          rel: "canonical",
          href: completeCanonical,
          "data-id": "layout_index_Layout_link"
        }, "canonical")]
      })]
    }), /*#__PURE__*/_jsxs(CentresContext.Provider, {
      value: (_header$data$centres = header === null || header === void 0 ? void 0 : (_header$data = header.data) === null || _header$data === void 0 ? void 0 : _header$data.centres) !== null && _header$data$centres !== void 0 ? _header$data$centres : [],
      children: [/*#__PURE__*/_jsxs("nav", {
        "aria-label": "Header",
        "data-id": "layout_index_Layout_nav",
        children: [showChrome && showCVPStrip && /*#__PURE__*/_jsx(CVPStrip, {
          country: country,
          "data-id": "layout_index_Layout_CVPStrip"
        }), showChrome && /*#__PURE__*/_jsx(Header, {
          "data-id": "layout_index_Layout_Header"
        }), pageTitle && /*#__PURE__*/_jsx(CoreContainer, {
          "data-id": "layout_index_Layout_CoreContainer",
          children: /*#__PURE__*/_jsx(H1, {
            sx: {
              marginBottom: 'spacing-sm'
            },
            "data-id": "layout_index_Layout_CoreContainer_H1",
            children: pageTitle
          })
        })]
      }), /*#__PURE__*/_jsx("main", {
        id: "main-content",
        style: mainStyles,
        "data-id": "layout_index_Layout_main",
        children: /*#__PURE__*/_jsx(CentreContext.Provider, {
          value: centre,
          children: /*#__PURE__*/_jsx(NavContext.Provider, {
            value: setHeaderNavigationPath,
            children: children
          })
        })
      }), showChrome && showFooter && /*#__PURE__*/_jsx(Box, {
        sx: {
          marginTop: ['spacing-4xl', null, 'spacing-5xl']
        },
        "data-id": "layout_index_Layout_Box",
        children: /*#__PURE__*/_jsx(UniversalFooter, {
          Link: Link,
          centre: centre,
          country: country,
          onFindACentre: () => setHeaderNavigationPath(['find-a-centre']),
          "data-id": "layout_index_Layout_Box_UniversalFooter"
        })
      })]
    })]
  });
}